import { AxiosResponse } from 'axios';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from 'react-query';
import serviceService, {
  IFetchServiceParams,
} from '../../services/serviceService';
import { IService } from '../../shared/interfaces/service-interfaces';

enum ServiceKeys {
  listServices = 'listServices',
}

const fetchServices = async (params?: IFetchServiceParams) => {
  const response = await serviceService.list(params);

  return response.data;
};

export const useGetServices = (
  params?: IFetchServiceParams,
): UseQueryResult<IService[]> => {
  return useQuery(ServiceKeys.listServices, () => fetchServices(params), {
    staleTime: 1000 * 60 * 3,
    keepPreviousData: false,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

interface IMutationProps {
  onSuccess?: () => {};
}

export const useCreateService: (
  props: IMutationProps,
) => UseMutationResult<
  AxiosResponse<IService>,
  Error,
  Omit<IService, 'id'>
> = ({ onSuccess }) => {
  const queryClient = useQueryClient();

  return useMutation(
    (data: Omit<IService, 'id'>) => serviceService.create(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(ServiceKeys.listServices);
        if (onSuccess) onSuccess();
      },
    },
  );
};

interface IMutationUpdateProps extends IMutationProps {
  serviceId: string;
}

export const useUpdateService: (
  props: IMutationUpdateProps,
) => UseMutationResult<
  AxiosResponse<IService>,
  Error,
  Omit<IService, 'id'>
> = ({ onSuccess, serviceId }) => {
  const queryClient = useQueryClient();

  return useMutation(
    (data: Omit<IService, 'id'>) => serviceService.update(serviceId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(ServiceKeys.listServices);
        if (onSuccess) onSuccess();
      },
    },
  );
};

interface IMutationDeleteProps extends IMutationProps {
  serviceId: string;
}

export const useDeleteService: (
  props: IMutationDeleteProps,
) => UseMutationResult<AxiosResponse, Error> = ({ onSuccess, serviceId }) => {
  const queryClient = useQueryClient();

  return useMutation(() => serviceService.delete(serviceId), {
    onSuccess: () => {
      queryClient.invalidateQueries(ServiceKeys.listServices);
      if (onSuccess) onSuccess();
    },
  });
};
